import { useState, type ReactNode } from 'react'

// Native HTML5 drag and drop: no library, and cards stay ordinary links/buttons so
// click-to-open and keyboard focus keep working. The dataTransfer type is private to
// this app so a file or text dragged in from elsewhere is never treated as a card.
export const CARD_MIME = 'application/x-bfg-card'

export function dragProps(id: string, onDragState?: (dragging: boolean) => void) {
  return {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData(CARD_MIME, id)
      e.dataTransfer.effectAllowed = 'move'
      onDragState?.(true)
    },
    onDragEnd: () => onDragState?.(false),
  }
}

/** A board column that accepts dropped cards. `onDrop` receives the card id. */
export function KanbanColumn({ label, onDrop, children, header, innerRef, className = '', style }: {
  label: string
  onDrop: (cardId: string) => void
  header: ReactNode
  children: ReactNode
  /** The board measures its columns to reorder them, and carries the one being
   *  dragged along with the pointer. */
  innerRef?: (el: HTMLElement | null) => void
  className?: string
  style?: React.CSSProperties
}) {
  const [over, setOver] = useState(0)
  return (
    <section
      ref={innerRef}
      className={`kcol${over > 0 ? ' drop-target' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      aria-label={label}
      onDragEnter={(e) => {
        if (!e.dataTransfer.types.includes(CARD_MIME)) return
        e.preventDefault()
        setOver((n) => n + 1)
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(CARD_MIME)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
      }}
      // enter/leave fire for every child element, so count rather than toggle
      onDragLeave={() => setOver((n) => Math.max(0, n - 1))}
      onDrop={(e) => {
        const id = e.dataTransfer.getData(CARD_MIME)
        setOver(0)
        if (!id) return
        e.preventDefault()
        onDrop(id)
      }}
    >
      {header}
      {children}
    </section>
  )
}
